import { SharedSlice } from 'src/components/slice'
import { StateCreator } from 'zustand'
import { PackageDetail } from '../../_common/interfaces'
import { Dayjs } from 'dayjs'
import { mercuryApi } from '$api'
import useAppStore from '$appStore'
import { exceptionMessageHandler, PermissionType } from '$utils'
import { IClockinResponse, IPaySummaryReportResponse } from '$models'
import { paySummaryService } from '$services'
import { saveClockin } from '../../_common/helpers'

interface PaySummaryState {
  paySummaryReport?: IPaySummaryReportResponse
  allPackages: PackageDetail[]
}

export interface PaySummarySlice extends PaySummaryState {
  resetPaySummarySlice: () => void

  getPaySummaryReport: (
    startDateTime: Dayjs,
    endDateTime: Dayjs
  ) => Promise<void>

  getPackages: () => Promise<void>

  /**
   * Saves an edited shift and reloads the report, so the totals follow the change
   * @returns true when the save went through
   */
  updateClockin: (
    clockin: IClockinResponse,
    successMessage?: string,
    errorMessage?: string
  ) => Promise<boolean>
}

const initialState: PaySummaryState = {
  paySummaryReport: undefined,
  allPackages: [],
}

/** The period the report currently shows, so an edited shift can reload the same one */
const getReportDateRange = (): [Dayjs, Dayjs] => {
  const { startDateTime, endDateTime } = useAppStore.getState().reportsMetadata
  return [startDateTime, endDateTime]
}

const createPaySummarySlice: StateCreator<
  PaySummarySlice & SharedSlice,
  [],
  [],
  PaySummarySlice
> = (set, get) => ({
  ...initialState,

  resetPaySummarySlice: () => set(initialState),

  getPaySummaryReport: async (
    startDateTime: Dayjs,
    endDateTime: Dayjs
  ): Promise<void> => {
    return paySummaryService
      .getReport(startDateTime, endDateTime)
      .then((report) => set(() => ({ paySummaryReport: report })))
      .catch((error) => {
        set(() => ({ paySummaryReport: undefined }))
        exceptionMessageHandler(error, 'Failed to retrieve the pay summary')
      })
  },

  getPackages: async (): Promise<void> => {
    const { activeCompany } = useAppStore.getState()

    const hasPayrollPermission = activeCompany?.hasPermission(
      PermissionType.Payroll
    )

    if (!hasPayrollPermission) {
      return set(() => ({ allPackages: [] }))
    }

    return mercuryApi
      .get<PackageDetail[]>(`/pay-rules/packages`)
      .then((result) => set(() => ({ allPackages: result })))
      .catch((error) => {
        set(() => ({ allPackages: [] }))
        exceptionMessageHandler(error, 'Failed to retrieve packages')
      })
  },

  updateClockin: async (
    clockin: IClockinResponse,
    successMessage: string = 'The shift has been updated',
    errorMessage: string = 'Failed to update the shift'
  ): Promise<boolean> =>
    saveClockin({
      clockin,
      getClockins: () =>
        (get().paySummaryReport?.staff ?? []).flatMap(
          (staff) => staff.clockins
        ),
      refetchAfterSave: () =>
        get().getPaySummaryReport(...getReportDateRange()),
      setLoading: get().setIsLoading,
      successMessage,
      errorMessage,
    }),
})

export default createPaySummarySlice
